/**
 * Created by rathompson on 10/13/23
 * File: MoveTenantSelectContent.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import { Body, Button, classNames, ModalBody, ModalFooter } from '@panwds/react-ui'
import StringTemplate from '../../../common/utils/StringTemplate'
import { LABEL_CURRENT_PARENT, LABEL_SELECT_MOVE_PARENT } from '../../constants/LabelConstants'
import TenantSearch from './TenantSearch'
import Tree from 'rc-tree'
import { BUTTON_CANCEL, BUTTON_CONTINUE } from '../../../common/constants/LabelConstants'
import { CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { Tsg } from '@sparky/framework'
import { isElement } from '../../../common/utils/DomUtils'
import { CaretRightIcon } from '@panwds/icons'
import GlobalContext from '../../../common/context/GlobalContext'

const MoveTenantSelectContent: VFC<{
  tsg: Tsg,
  onClose: any,
  onContinue: (selected: Tsg) => void
}> = ({ tsg, onClose, onContinue }) => {

  const { treeReducer } = useContext(GlobalContext)
  const { sgKeyBy: tsgMap } = treeReducer

  const [selected, setSelected] = useState<Tsg>()
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const treeRef = useRef<any>()

  const parent = tsgMap[tsg.parent_id as string]
  const rootTenant = useMemo(() => {
    let current = tsg
    while (!current.isRoot) {
      const parent_id = current.parent_id || ''
      current = tsgMap[parent_id]
    }
    return current
  }, [tsg, tsgMap])

  useEffect(() => {
    const defaultExpandedKeys = []
    if (rootTenant) {
      defaultExpandedKeys.push(rootTenant.id)
    }
    if (parent) {
      defaultExpandedKeys.push(parent.id)
    }
    setExpandedKeys(defaultExpandedKeys)
  }, [parent, rootTenant])

  const treeData = useMemo(() => {

    const getTitle: (node: Tsg, disabled?: boolean) => any = (node, disabled = false) => {
      const classNames = [
        'cm-truncate'
      ]
      if (disabled) {
        classNames.push('cm-text-secondary')
        // classNames.push('cm-cursor-not-allowed')
      }
      const title = node.id === parent?.id ? StringTemplate(LABEL_CURRENT_PARENT, [node.display_name]) : node.display_name
      return (
        <Body addClassName={classNames.join(' ')}>{title}</Body>
      )
    }

    const getTreeData: (children: Tsg[]) => any = (children) => {
      return children?.filter(each => each.id !== tsg.id).map((each) => {
        const disabled = each.id === parent?.id
        const style: CSSProperties = { width: 'inherit' }
        if (disabled) {
          // style['cursor'] = 'not-allowed'
        }
        return {
          ...each,
          key: each.id,
          style,
          title: getTitle(each, disabled),
          children: each.children ? getTreeData(each.children) : undefined,
          isLeaf: !each.children,
          disabled,
          'data-tsgid': each.id,
          'data-testid': `tsg-id:${each.id}`,
        }
      }) || []
    }

    if (rootTenant) {
      return getTreeData([rootTenant])
    }
    return []
  }, [parent?.id, rootTenant, tsg.id])

  const getSwitcherIcon = useCallback((arg) => {
    if (arg?.isLeaf) {
      return null
    }
    const className = classNames(arg?.expanded && 'cm-rotate-90')
    return <CaretRightIcon size='xxs' className={className} />
  }, [])

  const onExpand = useCallback((keys, event) => {
    if (event.expanded) {
      setExpandedKeys(keys)
    }
    else {
      const filterChildren: (children: Tsg[]) => any = (children) => {
        for (const child of children) {
          if (keys.includes(child.id)) {
            keys.splice(keys.indexOf(child.id), 1)
          }
          if (child.children) {
            filterChildren(child.children)
          }
        }
      }
      const selected = tsgMap[event.node.id]
      if (selected.children) {
        filterChildren(selected.children)
      }
      setExpandedKeys(keys)
    }
  }, [tsgMap])

  const scrollToRow = useCallback((tsg_id) => {
    setTimeout(() => {
      const tree = treeRef.current
      if (tree && isElement(tree)) {
        const element = tree.querySelector(`[data-tsgid="${tsg_id}"]`)
        if (element && isElement(element)) {
          if ('scrollIntoViewIfNeeded' in element) {
            (element as any).scrollIntoViewIfNeeded(true)
          }
          else {
            element.scrollIntoView?.({ inline: 'start', block: 'center' })
          }
        }
      }
    }, 100)
  }, [])

  const onSelectTenant = useCallback((tsg_id: string) => {
    setSelected(tsgMap[tsg_id])
    if (!expandedKeys.includes(tsg_id)) {
      setExpandedKeys([...expandedKeys, tsg_id])
      scrollToRow(tsg_id)
    }
  }, [expandedKeys, scrollToRow, tsgMap])

  return (
    <>
      <ModalBody addClassName='cm-overflow-hidden cm-flex cm-flex-col'>
        <Body addClassName='cm-pl-2 cm-pr-2 cm-pb-2'>{StringTemplate(LABEL_SELECT_MOVE_PARENT, [tsg.display_name])}</Body>
        <div className='cm-flex cm-flex-col cm-border cm-border-solid cm-rounded cm-border-gray-300 cm-overflow-hidden' style={{ height: '24rem' }}>
          <div className='cm-flex cm-flex-col'>
            <TenantSearch onSelectTenant={(id: string) => onSelectTenant(id)} treeData={treeData} />
          </div>
          <div className='cm-overflow-auto' ref={treeRef}>
            <Tree
              style={{ minHeight: '200px' }}
              prefixCls='tenant-tree'
              showIcon={false}
              onSelect={([id]) => onSelectTenant(id as string)}
              onExpand={onExpand}
              expandedKeys={expandedKeys}
              autoExpandParent
              treeData={treeData as any}
              switcherIcon={getSwitcherIcon}
              selectedKeys={[selected?.id].filter(Boolean) as string[]}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={onClose}
        >{BUTTON_CANCEL}</Button>
        <Button
          onClick={() => onContinue(selected as Tsg)}
          appearance='primary'
          disabled={!selected}
        >{BUTTON_CONTINUE}</Button>
      </ModalFooter>
    </>
  )
}

export default MoveTenantSelectContent
